import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ExcludeCellIcon } from '@/icons/ExcludeCellIcon'
import { IncludeCellIcon } from '@/icons/IncludeCellIcon'
import {
	handleBulkManualFilterChange,
	handleBulkManualFilterClear,
	manualExclusionFiltersSelector,
	manualInclusionFiltersSelector,
} from '@/redux/store/slices/grid/gridSlice'
import {
	selectedCellsSelector,
	setSelectedCells,
	setStatsTableColumnVisibility,
	statsTableColumnVisibilitySelector,
} from '@/redux/store/slices/map/mapSlice'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { Settings2, Trash2, X } from 'lucide-react'
import { useCallback, useMemo } from 'react'

import { formatDemographics } from '../Map/functions'
import { useTargetingSelection } from '../Map/hooks'
import { getColumns } from './columns'
import { DataTable } from './data-table'

import type { RowSelectionState, VisibilityState } from '@tanstack/react-table'
import type { AggregatedDemographics, ReferenceFields } from 'honeygrid-types'
import type { DataSourceFieldMappings } from 'honeygrid-types/validators-and-types/DataSourceTypes'
import type { FC } from 'react'

interface StatsTableProps {
	open: boolean
	aggregatedDemographics?: AggregatedDemographics
	tableReferenceField: ReferenceFields
	handleClose: () => void
	customKeys?: string[]
	fieldMappings?: DataSourceFieldMappings
}

export interface StatsTableData {
	id: string
	customerCount: number | null
	sales: number | null
	customerMarketShare: string | null
	households: number | null
	newMovers: number | null
	income: string | null
	homeValues: string | null
	sfdu: number | null
	mfdu: number | null
	[key: string]: string | number | null
}

export const StatsTable: FC<StatsTableProps> = ({
	open,
	aggregatedDemographics,
	tableReferenceField,
	handleClose,
	customKeys,
	fieldMappings,
}) => {
	const dispatch = useAppDispatch()
	const manualInclusionFilters = useAppSelector(manualInclusionFiltersSelector)
	const manualExclusionFilters = useAppSelector(manualExclusionFiltersSelector)
	const selectedCells = useAppSelector(selectedCellsSelector)
	const columnVisibility = useAppSelector(statsTableColumnVisibilitySelector)
	const targetingSelection = useTargetingSelection(aggregatedDemographics)

	const rows = useMemo(() => {
		if (!aggregatedDemographics) {
			return []
		}

		const sortedCellIds =
			aggregatedDemographics.sortedCellIds[tableReferenceField || 'households'] ?? []
		return sortedCellIds.map((cellId) => {
			const cell = aggregatedDemographics.cells[cellId]
			const {
				customerCount,
				sales,
				marketShare,
				households,
				newMovers,
				income,
				homeValues,
				sfdu,
				mfdu,
			} = formatDemographics(cell)

			const hasCustomData = customKeys && customKeys.length > 0

			const data: StatsTableData = {
				id: cellId,
				customerCount:
					hasCustomData && customKeys.includes('customerCount') ? (customerCount ?? null) : null,
				sales: hasCustomData && customKeys.includes('sales') ? (sales ?? null) : null,
				customerMarketShare:
					hasCustomData && customKeys.includes('customerMarketShare') ? marketShare : null,
				households: households ?? null,
				newMovers: newMovers ?? null,
				income: income?.toString() ?? null,
				homeValues: homeValues?.toString() ?? null,
				sfdu: sfdu ?? null,
				mfdu: mfdu ?? null,
			}

			return data
		})
	}, [aggregatedDemographics, customKeys, tableReferenceField])

	const columns = useMemo(
		() =>
			getColumns({
				targetingSelection,
				manualInclusionFilters,
				manualExclusionFilters,
				customKeys,
				fieldMappings,
			}),
		[targetingSelection, manualInclusionFilters, manualExclusionFilters, customKeys, fieldMappings]
	)

	// Convert selectedCells array to row selection state object
	const rowSelection = useMemo(() => {
		return selectedCells.reduce((acc, id) => {
			acc[id] = true
			return acc
		}, {} as RowSelectionState)
	}, [selectedCells])

	const handleRowSelectionChange = useCallback(
		(updater: any) => {
			const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater
			const selectedIds = Object.entries(newSelection)
				.filter(([_, selected]) => selected)
				.map(([id]) => id)
			dispatch(setSelectedCells(selectedIds))
		},
		[dispatch, rowSelection]
	)

	const handleColumnVisibilityChange = useCallback(
		(updaterOrValue: VisibilityState | ((state: VisibilityState) => VisibilityState)) => {
			const newState =
				typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibility) : updaterOrValue
			dispatch(setStatsTableColumnVisibility(newState))
		},
		[dispatch, columnVisibility]
	)

	const table = useReactTable({
		data: rows,
		columns,
		getCoreRowModel: getCoreRowModel(),
		onRowSelectionChange: handleRowSelectionChange,
		onColumnVisibilityChange: handleColumnVisibilityChange,
		getRowId: (row) => row.id,
		getSortedRowModel: getSortedRowModel(),
		state: {
			rowSelection,
			columnVisibility,
		},
	})

	return (
		<div className="fixed inset-0 pointer-events-none">
			<Card
				className={`p-0 gap-0 fixed bottom-0 left-0 right-0 z-[1] h-[50vh] rounded-none border-t bg-background pointer-events-auto transform transition-transform duration-300 ease-in-out ${
					open ? 'translate-y-0' : 'translate-y-full'
				}`}
			>
				<div className="flex items-center justify-between gap-2 p-4 border-b">
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							className="cursor-pointer"
							disabled={!selectedCells.length}
							onClick={() => {
								dispatch(
									handleBulkManualFilterChange({
										cellIds: selectedCells,
										selectedTool: 'inclusion',
									})
								)
							}}
						>
							<IncludeCellIcon className="mr-2 h-4 w-4" />
							Include
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="cursor-pointer"
							disabled={!selectedCells.length}
							onClick={() => {
								dispatch(
									handleBulkManualFilterChange({
										cellIds: selectedCells,
										selectedTool: 'exclusion',
									})
								)
							}}
						>
							<ExcludeCellIcon className="mr-2 h-4 w-4" />
							Exclude
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="cursor-pointer"
							disabled={!selectedCells.length}
							onClick={() => {
								dispatch(
									handleBulkManualFilterClear({
										cellIds: selectedCells,
									})
								)
							}}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							Clear overrides
						</Button>
						<div className="ml-4 text-sm text-muted-foreground flex items-center">
							{selectedCells.length > 0 && (
								<span>
									<strong>{selectedCells.length.toLocaleString()}</strong> cells selected
								</span>
							)}
						</div>
					</div>
					<div className="flex gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm" className="h-8">
									<Settings2 className="mr-2 h-4 w-4" />
									View
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{table
									.getAllColumns()
									.filter((column) => column.id !== 'select' && column.getCanHide())
									.map((column) => (
										<DropdownMenuCheckboxItem
											key={column.id}
											className="capitalize"
											checked={column.getIsVisible()}
											onCheckedChange={column.toggleVisibility}
										>
											{column.columnDef.header?.toString() || column.id}
										</DropdownMenuCheckboxItem>
									))}
							</DropdownMenuContent>
						</DropdownMenu>
						<Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
							<X className="h-4 w-4" />
						</Button>
					</div>
				</div>
				<div className="h-full overflow-auto">{open && <DataTable table={table} />}</div>
			</Card>
		</div>
	)
}
